import { api, type ChapterItem, type PluginInfo, type RedactRule } from '../api';
import { qs, toast, switchToPrettyUrl, switchToPrettyUrlSync, getShareUrl } from '../utils';
import { getCachedSlug } from '../pretty-cache';
import { chapterUrlSuffix } from '../chapter-volume';
import { prettyWorkPath } from '../pretty-path';
import { chapterLoadingHtml, trackChapterProgress } from '../chapter-loading';
import { getCurrentUser } from '../auth';
import { markGuestRead } from '../guest-read';

interface ReaderSettings {
  fontSize: number;
  themeIndex: number;
  align: 'left' | 'center' | 'justify' | 'right';
  lineHeight: number;
  padding: number;
  topOffset: number;
  fontFamily: string;
  mangaMode: 'continuous' | 'paged';
  sliderOffset: number;
}

const THEMES = [
  { bg: '#ffffff', text: '#1a1a1a' },
  { bg: '#f1e4c8', text: '#3a2c1a' },
  { bg: '#dcecdd', text: '#1c2b1c' },
  { bg: '#181a24', text: '#eceaf1' },
  { bg: '#000000', text: '#c9c9c9' },
];
const FONT_FAMILIES = ['Original', 'Lora', 'Nunito', 'Merriweather'];
const FONT_CSS: Record<string, string> = {
  Original: 'inherit',
  Lora: "'Lora', Georgia, serif",
  Nunito: "'Nunito', sans-serif",
  Merriweather: "'Merriweather', Georgia, serif",
};

// Рекомендоване значення верхнього відступу: достатньо, щоб перший рядок
// тексту не опинявся під вирізом камери (notch/punch-hole) на більшості
// телефонів, і водночас не залишало зайвої "порожнечі" зверху на екранах
// без вирізу. Показується як підказка в панелі налаштувань нижче.
const RECOMMENDED_TOP_OFFSET = 29;

// Додатковий відступ бічного повзунка розділу від контентної зони (0 = поведінка
// за замовчуванням з CSS: впритул до тексту на телефоні, до --zone-right на ПК).
// Користувач регулює це в налаштуваннях читалки ("Відступ повзунка"), слайдер
// нижче обмежує його розумним діапазоном 0–20px.
const MAX_SLIDER_OFFSET = 20;

// Індекси в THEMES: 0 — білий, 3 — темний (#181a24).
const LIGHT_THEME_INDEX = 0;
const DARK_THEME_INDEX = 3;
const prefersLight = () => window.matchMedia?.('(prefers-color-scheme: light)').matches ?? false;

const DEFAULT_SETTINGS: ReaderSettings = {
  fontSize: 16,
  themeIndex: DARK_THEME_INDEX,
  align: 'left',
  lineHeight: 1.5,
  padding: 16,
  topOffset: RECOMMENDED_TOP_OFFSET,
  fontFamily: 'Original',
  mangaMode: 'continuous',
  sliderOffset: 0,
};

const STORAGE_KEY = 'yomukoma:reader-settings';

// true, якщо колір фону вже налаштовано (збережений раніше або обраний у панелі налаштувань).
// Поки false — фон іде за темою ОС (світла → білий, темна → темний) і в сховище не пишеться,
// тож обраний користувачем колір фону ніколи не підміняється.
let themeExplicit = false;

function loadSettings(): ReaderSettings {
  const defaults = { ...DEFAULT_SETTINGS, themeIndex: prefersLight() ? LIGHT_THEME_INDEX : DARK_THEME_INDEX };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      themeExplicit = typeof saved?.themeIndex === 'number' && !!THEMES[saved.themeIndex];
      const merged = { ...defaults, ...saved };
      if (!themeExplicit) merged.themeIndex = defaults.themeIndex;
      return merged;
    }
  } catch {
    /* ігноруємо */
  }
  return defaults;
}
function saveSettings(s: ReaderSettings) {
  try {
    const out: Partial<ReaderSettings> = { ...s };
    if (!themeExplicit) delete out.themeIndex;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
  } catch {
    /* ігноруємо (наприклад, приватний режим браузера) */
  }
}

function extractImageSrcs(html: string): string[] {
  const div = document.createElement('div');
  div.innerHTML = html;
  return Array.from(div.querySelectorAll('img'))
    .map((img) => img.getAttribute('src') || '')
    .filter(Boolean);
}

/**
 * Позначає кожне зображення в контейнері класом content-img-loaded, коли
 * воно довантажиться (або впаде з помилкою) — до цього діє CSS-плейсхолдер
 * (сірий прямокутник фіксованої мінімальної висоти), який плавно змінюється
 * на саме зображення. Головна мета — щоб висота контейнера не "стрибала" з
 * майже нуля до реальної вже ПІСЛЯ того, як ми порахували позицію
 * відновлення чи прогрес читання.
 */
function attachContentImageLoadHandlers(container: HTMLElement): HTMLImageElement[] {
  const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  imgs.forEach((img) => {
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('content-img-loaded');
      return;
    }
    const markLoaded = () => img.classList.add('content-img-loaded');
    img.addEventListener('load', markLoaded, { once: true });
    img.addEventListener('error', markLoaded, { once: true });
  });
  return imgs;
}

/** Чекає, поки всі <img> у контейнері довантажаться (або впадуть з помилкою), з тайм-аутом. */
function waitForImages(container: HTMLElement, timeoutMs = 6000): Promise<void> {
  const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  const pending = imgs.filter((img) => !(img.complete && img.naturalWidth > 0));
  if (!pending.length) return Promise.resolve();
  const allLoaded = Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
  return Promise.race([allLoaded, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

export async function initReader() {
  const pluginId = qs('plugin');
  const novelPath = qs('novelPath');
  const novelName = qs('novelName') || '';
  let chapterPath = qs('path');
  let chapterName = qs('name') || '';
  let chapterNumParam = qs('num');
  const cover = qs('cover') || undefined;
  // "Продовжити читання" (novel.html) може передати збережену позицію в розділі:
  // progress — % прогортаного тексту (novel), page — номер сторінки (manga, 1-based).
  const restoreProgressParam = qs('progress');
  const restorePageParam = qs('page');
  // Звідки прийшли (Історія/Нове/Бібліотека) — щоб кнопка "назад" на сторінці
  // твору вела туди ж, а не завжди на сторінку джерела. Якщо прийшли з
  // Бібліотеки — прокидаємо також категорію/сортування/пошук, у яких вона
  // була, щоб не просто повернутись у бібліотеку, а в той самий її стан.
  const fromParam = qs('from');
  const libReturnParams = new URLSearchParams();
  if (fromParam === 'library') {
    for (const key of ['tab', 'sort', 'q']) {
      const v = qs(key);
      if (v) libReturnParams.set(key, v);
    }
  }
  const fromSuffix =
    (fromParam ? `&from=${encodeURIComponent(fromParam)}` : '') +
    (libReturnParams.toString() ? `&${libReturnParams.toString()}` : '');

  // Синхронно, ДО завантаження вмісту розділу — якщо слаг твору вже
  // відомий (кеш або сама сторінка відкрита за коротким посиланням),
  // адреса одразу коротка, без "спалаху" старого вигляду.
  if (pluginId && novelPath && chapterNumParam) {
    switchToPrettyUrlSync(pluginId, novelPath, Number(chapterNumParam));
  }

  const root = {
    topbar: document.getElementById('reader-topbar-chrome') as HTMLElement,
    bottombar: document.getElementById('reader-bottombar-chrome') as HTMLElement,
    vSlider: document.getElementById('v-slider') as HTMLElement,
    content: document.getElementById('reader-content') as HTMLElement,
    text: document.getElementById('reader-text') as HTMLElement,
    mangaPaged: document.getElementById('manga-paged') as HTMLElement,
    mangaImg: document.getElementById('manga-page-img') as HTMLImageElement,
    backLink: document.getElementById('back-link') as HTMLAnchorElement,
    title: document.getElementById('reader-title') as HTMLElement,
    subtitle: document.getElementById('reader-subtitle') as HTMLElement,
    searchBtn: document.getElementById('search-btn') as HTMLElement,
    redactBtn: document.getElementById('redact-btn') as HTMLElement,
    moreBtn: document.getElementById('more-btn') as HTMLElement,
    moreMenu: document.getElementById('more-menu') as HTMLElement,
    openBrowserBtn: document.getElementById('open-browser-btn') as HTMLElement,
    shareBtn: document.getElementById('share-btn') as HTMLElement,
    searchBar: document.getElementById('search-bar') as HTMLElement,
    searchInput: document.getElementById('search-input') as HTMLInputElement,
    searchCount: document.getElementById('search-count') as HTMLElement,
    searchPrev: document.getElementById('search-prev') as HTMLElement,
    searchNext: document.getElementById('search-next') as HTMLElement,
    searchClose: document.getElementById('search-close') as HTMLElement,
    prevBtn: document.getElementById('prev-btn') as HTMLButtonElement,
    topBtn: document.getElementById('top-btn') as HTMLElement,
    chlistBtn: document.getElementById('chlist-btn') as HTMLElement,
    settingsBtn: document.getElementById('settings-btn') as HTMLElement,
    nextBtn: document.getElementById('next-btn') as HTMLButtonElement,
    nextChapterFab: document.getElementById('next-chapter-fab') as HTMLButtonElement,
    progressFill: document.getElementById('reader-progress-fill') as HTMLElement,
    progressPct: document.getElementById('reader-progress-pct') as HTMLElement,
    vTrack: document.getElementById('v-slider-track') as HTMLElement,
    vFill: document.getElementById('v-slider-fill') as HTMLElement,
    vThumb: document.getElementById('v-slider-thumb') as HTMLElement,
    vBubble: document.getElementById('v-slider-bubble') as HTMLElement,
    vTopLabel: document.getElementById('v-slider-top-label') as HTMLElement,
    vBottomLabel: document.getElementById('v-slider-bottom-label') as HTMLElement,
    chaptersOverlay: document.getElementById('chapters-overlay') as HTMLElement,
    chaptersClose: document.getElementById('chapters-close') as HTMLElement,
    chaptersListPanel: document.getElementById('chapters-list-panel') as HTMLElement,
    scrollUpBtn: document.getElementById('scroll-up-btn') as HTMLElement,
    scrollDownBtn: document.getElementById('scroll-down-btn') as HTMLElement,
    settingsOverlay: document.getElementById('reader-settings-overlay') as HTMLElement,
    settingsTabs: document.querySelectorAll<HTMLElement>('.settings-tab'),
    settingsPanelReader: document.getElementById('settings-tab-reader') as HTMLElement,
    redactOverlay: document.getElementById('redact-overlay') as HTMLElement,
    redactClose: document.getElementById('redact-close') as HTMLElement,
    redactListChapter: document.getElementById('redact-list-chapter') as HTMLElement,
    redactListNovel: document.getElementById('redact-list-novel') as HTMLElement,
    redactFromInput: document.getElementById('redact-from-input') as HTMLInputElement,
    redactToInput: document.getElementById('redact-to-input') as HTMLInputElement,
    redactScopeSegmented: document.getElementById('redact-scope-segmented') as HTMLElement,
    redactAddBtn: document.getElementById('redact-add-btn') as HTMLButtonElement,
    imageViewerOverlay: document.getElementById('image-viewer-overlay') as HTMLElement,
    imageViewerStage: document.getElementById('image-viewer-stage') as HTMLElement,
    imageViewerImg: document.getElementById('image-viewer-img') as HTMLImageElement,
    imageViewerClose: document.getElementById('image-viewer-close') as HTMLElement,
  };

  if (!pluginId || !novelPath || !chapterPath) {
    root.text.innerHTML = '<p>Не вказано параметри розділу.</p>';
    return;
  }

  root.backLink.href = `/novel?plugin=${encodeURIComponent(pluginId)}&path=${encodeURIComponent(novelPath)}${fromSuffix}`;
  root.title.textContent = novelName;
  root.subtitle.textContent = chapterName;
  document.title = chapterName || novelName;

  // Тихо підміняємо адресу на коротке посилання /<джерело>/<слаг>/chapterN
  // (лише якщо знаємо порядковий номер розділу — саме він визначає N).
  if (novelName && chapterNumParam) {
    switchToPrettyUrl(pluginId, novelPath, novelName, Number(chapterNumParam));
  }

  // --- визначаємо тип джерела (novel/manga) ---
  let contentType: 'novel' | 'manga' | 'anime' = 'novel';
  try {
    const plugins = await api.plugins();
    const info = plugins.find((p: PluginInfo) => p.id === pluginId);
    if (info) contentType = info.contentType;
  } catch {
    /* залишаємось з novel за замовчуванням */
  }
  root.searchBtn.style.display = contentType === 'novel' ? 'flex' : 'none';
  // Гість не бачить кнопки редагування тексту (правки зберігаються в акаунті; API /api/redact — лише для авторизованих).
  const isGuest = !(await getCurrentUser());
  document.body.classList.toggle('guest-mode', isGuest);
  root.redactBtn.style.display = contentType === 'novel' && !isGuest ? 'flex' : 'none';
  if (contentType === 'manga') root.content.classList.add('manga-content');

  const settings = loadSettings();
  // Якщо колір фону не налаштовано — стежимо за зміною теми ОС (світла/темна) на льоту.
  window.matchMedia?.('(prefers-color-scheme: light)').addEventListener?.('change', () => {
    if (themeExplicit) return;
    settings.themeIndex = prefersLight() ? LIGHT_THEME_INDEX : DARK_THEME_INDEX;
    if (contentType === 'novel') applyTypographySettings();
  });

  // Відступ повзунка застосовується через CSS-змінну (--slider-user-offset, style.css)
  // одразу для всіх правил позиціонування .v-slider (телефон/ПК, портрет/ландшафт),
  // тож рахувати його окремо для кожного медіа-запиту тут не потрібно. На відміну
  // від applyTypographySettings — стосується й новели, і манги, тож викликається
  // одразу після завантаження налаштувань, а не лише з відповідної панелі.
  function applySliderOffset() {
    document.documentElement.style.setProperty('--slider-user-offset', `${settings.sliderOffset}px`);
  }
  applySliderOffset();

  // --- список розділів (завантажується ОДИН раз і кешується — і для prev/next, і для панелі "Глави") ---
  let chapters: ChapterItem[] = [];
  try {
    const novel = await api.novel(pluginId, novelPath);
    chapters = novel.chapters ?? [];
  } catch {
    /* без списку — просто не буде навігації prev/next та панелі глав */
  }

  function chapterUrl(ch: ChapterItem): string {
    const extra = new URLSearchParams();
    if (fromParam) extra.set('from', fromParam);
    for (const [k, v] of libReturnParams) extra.set(k, v);

    const cachedSlug = getCachedSlug(pluginId!, novelPath!);
    if (cachedSlug && ch.chapterNumber !== undefined) {
      const qsStr = extra.toString();
      return `${prettyWorkPath(pluginId!, cachedSlug, chapterUrlSuffix(ch.chapterNumber))}${
        qsStr ? `?${qsStr}` : ''
      }`;
    }

    const params = new URLSearchParams({
      plugin: pluginId!,
      novelPath: novelPath!,
      novelName,
      path: ch.path,
      name: ch.name,
    });
    if (ch.chapterNumber !== undefined) params.set('num', String(ch.chapterNumber));
    if (cover) params.set('cover', cover);
    for (const [k, v] of extra) params.set(k, v);
    return `/reader?${params.toString()}`;
  }

  function goToChapter(ch: ChapterItem) {
    location.href = chapterUrl(ch);
  }

  // ---------------- "Назад до Бібліотеки" в кінці останнього розділу ----------------
  // Пігулка веде в Бібліотеку саме на той тип контенту (manga/novel), яким
  // читаємо зараз, і, якщо прийшли з Бібліотеки, у ту саму категорію/сортування/пошук.
  function libraryHref(type: 'manga' | 'novel'): string {
    const params = new URLSearchParams({ type });
    for (const [k, v] of libReturnParams) params.set(k, v);
    return `/library?${params.toString()}`;
  }

  function createBackToLibraryEl(type: 'manga' | 'novel'): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = `reader-end-library-wrap reader-end-library-${type}`;
    const a = document.createElement('a');
    a.className = `reader-end-library-btn type-${type}`;
    // Гість не має бібліотеки: замість "Назад до Бібліотеки" — "Назад", що повертає на
    // сторінку твору (та сама адреса, що й у кнопки "назад" зверху читалки).
    a.href = isGuest ? root.backLink.href : libraryHref(type);
    a.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg><span>' +
      (isGuest ? 'Назад' : 'Назад до Бібліотеки') +
      '</span>';
    wrap.appendChild(a);
    return wrap;
  }

  // ---------------- chrome (верх/низ/повзунок) — приховано за замовчуванням ----------------
  let chromeVisible = false;
  function setChrome(visible: boolean) {
    chromeVisible = visible;
    root.topbar.style.display = visible ? 'flex' : 'none';
    root.bottombar.style.display = visible ? 'flex' : 'none';
    root.vSlider.style.display = visible ? 'flex' : 'none';
    if (!visible) {
      root.moreMenu.style.display = 'none';
      root.searchBar.style.display = 'none';
    }
  }
  function toggleChrome() {
    setChrome(!chromeVisible);
  }

  root.content.addEventListener('click', (e) => {
    if (contentType === 'manga' && settings.mangaMode === 'paged') return; // обробляється окремо (ліва/права/центр зона)
    // Тап по посиланню чи кнопці всередині контенту — це навігація, а не тап "по тексту":
    // пігулка "Назад" в кінці розділу лежить усередині root.content, і без цієї перевірки її
    // клік спливав сюди, панелі читалки спалахували, а лише потім відбувався перехід.
    if ((e.target as HTMLElement).closest('a[href], button')) return;
    toggleChrome();
  });

  root.moreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    root.moreMenu.style.display = root.moreMenu.style.display === 'block' ? 'none' : 'block';
  });
  document.addEventListener('click', (e) => {
    if (!root.moreMenu.contains(e.target as Node) && e.target !== root.moreBtn) {
      root.moreMenu.style.display = 'none';
    }
  });

  let currentChapterSourceUrl: string | undefined;
  root.openBrowserBtn.addEventListener('click', () => {
    root.moreMenu.style.display = 'none';
    if (currentChapterSourceUrl) window.open(currentChapterSourceUrl, '_blank', 'noopener');
    else toast('Посилання недоступне');
  });
  root.shareBtn.addEventListener('click', async () => {
    root.moreMenu.style.display = 'none';
    // Посилання на ЦЕЙ розділ у читалці (коротке /<джерело>/<слаг>/chapterN), а не на сайт-джерело —
    // те є в пункті "Відкрити в браузері" (currentChapterSourceUrl).
    const shareUrl = await getShareUrl(
      pluginId,
      novelPath,
      novelName || document.title,
      chapterNumParam !== null && chapterNumParam !== '' ? Number(chapterNumParam) : undefined,
    );
    if (navigator.share) {
      try {
        await navigator.share({ title: chapterName || novelName, url: shareUrl });
      } catch {
        /* користувач скасував — нічого не робимо */
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast('Посилання скопійовано');
      } catch {
        toast(shareUrl);
      }
    }
  });

  // ---------------- пошук у тексті (лише novel) ----------------
  let matchEls: HTMLElement[] = [];
  let matchIndex = -1;

  function clearHighlights() {
    root.text.querySelectorAll('mark').forEach((m) => {
      const parent = m.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(m.textContent || ''), m);
      parent.normalize();
    });
    matchEls = [];
    matchIndex = -1;
  }

  function runSearch(term: string) {
    clearHighlights();
    if (!term.trim()) {
      root.searchCount.textContent = '';
      return;
    }
    const lower = term.toLowerCase();
    const walker = document.createTreeWalker(root.text, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let n: Node | null;
    while ((n = walker.nextNode())) textNodes.push(n as Text);

    for (const node of textNodes) {
      const text = node.textContent || '';
      const lowerText = text.toLowerCase();
      if (!lowerText.includes(lower)) continue;
      const frag = document.createDocumentFragment();
      let idx = 0;
      let pos: number;
      while ((pos = lowerText.indexOf(lower, idx)) !== -1) {
        if (pos > idx) frag.appendChild(document.createTextNode(text.slice(idx, pos)));
        const mark = document.createElement('mark');
        mark.textContent = text.slice(pos, pos + term.length);
        frag.appendChild(mark);
        matchEls.push(mark);
        idx = pos + term.length;
      }
      if (idx < text.length) frag.appendChild(document.createTextNode(text.slice(idx)));
      node.parentNode?.replaceChild(frag, node);
    }

    root.searchCount.textContent = matchEls.length ? `1/${matchEls.length}` : '0/0';
    matchIndex = matchEls.length ? 0 : -1;
    focusMatch();
  }

  function focusMatch() {
    root.text.querySelectorAll('mark.current-match').forEach((m) => m.classList.remove('current-match'));
    if (matchIndex < 0 || !matchEls[matchIndex]) return;
    const el = matchEls[matchIndex];
    el.classList.add('current-match');
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    root.searchCount.textContent = `${matchIndex + 1}/${matchEls.length}`;
  }

  root.searchBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    root.searchBar.style.display = root.searchBar.style.display === 'flex' ? 'none' : 'flex';
    if (root.searchBar.style.display === 'flex') {
      root.searchInput.focus();
    } else {
      clearHighlights();
      root.searchInput.value = '';
    }
  });
  let searchDebounce: number | undefined;
  root.searchInput.addEventListener('input', () => {
    window.clearTimeout(searchDebounce);
    searchDebounce = window.setTimeout(() => runSearch(root.searchInput.value), 300);
  });
  root.searchNext.addEventListener('click', () => {
    if (!matchEls.length) return;
    matchIndex = (matchIndex + 1) % matchEls.length;
    focusMatch();
  });
  root.searchPrev.addEventListener('click', () => {
    if (!matchEls.length) return;
    matchIndex = (matchIndex - 1 + matchEls.length) % matchEls.length;
    focusMatch();
  });
  root.searchClose.addEventListener('click', () => {
    root.searchBar.style.display = 'none';
    root.searchInput.value = '';
    clearHighlights();
  });

  // ---------------- "Редагувати" (redact): заміна тексту/назв ----------------
  // Лише для novel — кнопка прихована для manga (див. вище).
  let redactScope: RedactRule['scope'] = 'chapter';

  async function refreshChapterContent() {
    // Перезавантажує поточний розділ (уже з накладеними правилами заміни),
    // зберігаючи приблизну позицію прогортання.
    if (contentType !== 'novel') return;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const frac = scrollable > 0 ? window.scrollY / scrollable : 0;
    try {
      const { content, url } = await api.chapter(pluginId!, chapterPath!, novelPath!);
      currentChapterSourceUrl = url;
      root.text.innerHTML = content || '<p>Текст розділу порожній.</p>';
    } catch {
      toast('Не вдалося оновити текст розділу');
      return;
    }
    if (!nextChapter) root.text.appendChild(createBackToLibraryEl('novel'));
    setupChapterEndTrigger();
    const newScrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (newScrollable > 0) window.scrollTo({ top: frac * newScrollable });
  }

  function renderRedactList(container: HTMLElement, rules: RedactRule[], emptyText: string) {
    container.innerHTML = '';
    if (!rules.length) {
      const empty = document.createElement('div');
      empty.className = 'redact-empty';
      empty.textContent = emptyText;
      container.appendChild(empty);
      return;
    }
    rules.forEach((rule) => {
      const row = document.createElement('div');
      row.className = 'redact-item-row';
      const toHtml = rule.to ? escapeHtml(rule.to) : '<em>(вилучити)</em>';
      row.innerHTML =
        '<div class="redact-item-text">' +
        `<span class="ri-from">${escapeHtml(rule.from)}</span>` +
        '<svg class="ri-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
        `<span class="ri-to">${toHtml}</span>` +
        '</div>' +
        '<button type="button" class="redact-item-del" aria-label="Видалити заміну">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>';
      row.querySelector('.redact-item-del')!.addEventListener('click', async () => {
        try {
          await api.removeRedactRule(pluginId!, novelPath!, rule.id);
          await loadRedactLists();
          await refreshChapterContent();
          toast('Заміну видалено');
        } catch (err) {
          toast((err as Error).message);
        }
      });
      container.appendChild(row);
    });
  }

  async function loadRedactLists() {
    try {
      const { chapterRules, novelRules } = await api.getRedactRules(pluginId!, novelPath!, chapterPath!);
      renderRedactList(root.redactListChapter, chapterRules, 'Немає замін лише для цього розділу.');
      renderRedactList(root.redactListNovel, novelRules, 'Немає замін для всього твору.');
    } catch {
      toast('Не вдалося завантажити список замін');
    }
  }

  root.redactBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    root.redactOverlay.style.display = 'flex';
    loadRedactLists();
  });
  root.redactClose.addEventListener('click', () => (root.redactOverlay.style.display = 'none'));
  root.redactOverlay.addEventListener('click', (e) => {
    if (e.target === root.redactOverlay) root.redactOverlay.style.display = 'none';
  });
  root.redactScopeSegmented.querySelectorAll('button').forEach((btn) => {
    btn.addEventListener('click', () => {
      redactScope = ((btn as HTMLElement).dataset.v as RedactRule['scope']) || 'chapter';
      root.redactScopeSegmented.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });
  root.redactAddBtn.addEventListener('click', async () => {
    const from = root.redactFromInput.value;
    const to = root.redactToInput.value;
    if (!from.trim()) {
      toast('Вкажіть текст, який потрібно замінити');
      return;
    }
    try {
      await api.addRedactRule({
        pluginId: pluginId!,
        novelPath: novelPath!,
        chapterPath: redactScope === 'chapter' ? chapterPath! : undefined,
        chapterName: redactScope === 'chapter' ? chapterName : undefined,
        from,
        to,
        scope: redactScope,
      });
      root.redactFromInput.value = '';
      root.redactToInput.value = '';
      await loadRedactLists();
      await refreshChapterContent();
      toast('Заміну додано');
    } catch (err) {
      toast((err as Error).message);
    }
  });

  // ---------------- прогрес (постійний нижній індикатор + збереження) ----------------
  function paintProgress(pct: number) {
    root.progressFill.style.width = `${pct}%`;
    root.progressPct.textContent = `${pct}%`;
  }

  async function saveProgress(
    pluginIdArg: string,
    novelPathArg: string,
    chPath: string,
    chName: string,
    chNum: number | undefined,
    progress: number,
    page?: number,
    totalPages?: number,
  ) {
    // Гість: дочитаний розділ (100%) запам'ятовуємо в браузері окремо для кожного твору (guest-read.ts).
    if (isGuest && progress >= 100) markGuestRead(pluginIdArg, novelPathArg, chPath);
    try {
      await api.saveProgress({
        pluginId: pluginIdArg,
        novelPath: novelPathArg,
        novelName,
        cover,
        chapterPath: chPath,
        chapterName: chName,
        chapterNumber: chNum,
        progress,
        page,
        totalPages,
      });
    } catch {
      /* мовчки ігноруємо помилки збереження прогресу */
    }
  }

  // ---------------- вертикальний повзунок ----------------
  // Для manga повзунок завжди рахує сторінки (не відсотки) — і в неперервному (webtoon), і в посторінковому режимі.
  let sliderMode: 'scroll' | 'page' = contentType === 'manga' ? 'page' : 'scroll';
  let mangaImages: string[] = [];
  let pageIndex = 0;

  // ---- відстеження сторінок для неперервного (webtoon) режиму manga ----
  // Плаский список усіх завантажених зображень (початковий розділ + довантажені далі), у порядку документа.
  let allMangaImgs: { path: string; el: HTMLImageElement }[] = [];
  let activeMangaChapterPath: string | null = null;
  let activeMangaChapterTotal = 0;
  let activeMangaLocalIndex = 0;

  function tagChapterImages(path: string, container: HTMLElement): HTMLImageElement[] {
    const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
    imgs.forEach((img) => {
      img.dataset.chapterPath = path;
      allMangaImgs.push({ path, el: img });
    });
    return imgs;
  }

  function updateSliderFromValue(fraction: number) {
    const clamped = Math.min(1, Math.max(0, fraction));
    root.vFill.style.height = `${clamped * 100}%`;
    root.vThumb.style.top = `${clamped * 100}%`;
  }

  function refreshSliderLabels(total?: number) {
    if (sliderMode === 'page') {
      root.vTopLabel.textContent = '1';
      root.vBottomLabel.textContent = String(Math.max(1, total ?? mangaImages.length));
    } else {
      root.vTopLabel.textContent = '0';
      root.vBottomLabel.textContent = '100';
    }
  }

  // Малює прогрес для manga у сторінках ("3/20"), а не у відсотках.
  function paintMangaProgress(pageNum: number, total: number) {
    const pct = total > 1 ? Math.round(((pageNum - 1) / (total - 1)) * 100) : 100;
    root.progressFill.style.width = `${pct}%`;
    root.progressPct.textContent = `${pageNum}/${Math.max(1, total)}`;
    return pct;
  }

  let draggingSlider = false;
  function sliderPointerToFraction(clientY: number): number {
    const rect = root.vTrack.getBoundingClientRect();
    const fraction = (clientY - rect.top) / rect.height;
    return Math.min(1, Math.max(0, fraction));
  }

  // Текст бульбашки, що зʼявляється біля повзунка, поки його тримають і рухають: лише
  // саме число — номер сторінки (манґа) або відсоток прогорнутого тексту (новела), без
  // "/total" чи "%" (їх показують сусідні елементи — нижня панель прогресу й підписи
  // повзунка). Рахує ту саму позицію, що застосує applySliderFraction — лише як текст,
  // без побічних дій.
  function sliderBubbleText(fraction: number): string {
    if (contentType === 'manga' && settings.mangaMode === 'continuous') {
      const total = Math.max(1, activeMangaChapterTotal);
      return `${Math.round(fraction * (total - 1)) + 1}`;
    }
    if (sliderMode === 'page') {
      const total = Math.max(1, mangaImages.length);
      return `${Math.round(fraction * (total - 1)) + 1}`;
    }
    return `${Math.round(fraction * 100)}`;
  }

  root.vTrack.addEventListener('pointerdown', (e) => {
    draggingSlider = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const fraction = sliderPointerToFraction(e.clientY);
    applySliderFraction(fraction);
    root.vBubble.textContent = sliderBubbleText(fraction);
    root.vBubble.classList.add('visible');
  });
  root.vTrack.addEventListener('pointermove', (e) => {
    if (!draggingSlider) return;
    const fraction = sliderPointerToFraction(e.clientY);
    applySliderFraction(fraction);
    root.vBubble.textContent = sliderBubbleText(fraction);
  });
  root.vTrack.addEventListener('pointerup', () => {
    draggingSlider = false;
    root.vBubble.classList.remove('visible');
  });
  root.vTrack.addEventListener('pointercancel', () => {
    draggingSlider = false;
    root.vBubble.classList.remove('visible');
  });

  function applySliderFraction(fraction: number) {
    if (contentType === 'manga' && settings.mangaMode === 'continuous') {
      // Неперервний (webtoon) режим: повзунок вказує на сторінку (1 → перше зображення, 3 → третє і т.д.),
      // а не на частку прогортаних пікселів.
      const total = Math.max(1, activeMangaChapterTotal);
      const idx = Math.round(fraction * (total - 1));
      jumpToMangaPage(idx);
    } else if (sliderMode === 'page') {
      const idx = Math.round(fraction * Math.max(0, mangaImages.length - 1));
      showMangaPage(idx); // showMangaPage сам виставляє точну позицію повзунка під snapped-індекс
    } else {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({ top: fraction * scrollable });
      updateSliderFromValue(fraction);
    }
  }

  // Прокручує до початку N-ї сторінки (0-based) поточного активного розділу в неперервному режимі.
  function jumpToMangaPage(idx: number) {
    if (!activeMangaChapterPath) return;
    const pageEls = allMangaImgs.filter((e) => e.path === activeMangaChapterPath);
    if (!pageEls.length) return;
    const clamped = Math.min(Math.max(idx, 0), pageEls.length - 1);
    const target = pageEls[clamped].el;
    const rect = target.getBoundingClientRect();
    const top = window.scrollY + rect.top - 4;
    window.scrollTo({ top: Math.max(0, top) });
    paintMangaProgress(clamped + 1, pageEls.length);
    updateSliderFromValue(pageEls.length > 1 ? clamped / (pageEls.length - 1) : 1);
  }

  // ---------------- контент розділу ----------------
  function applyTypographySettings() {
    const theme = THEMES[settings.themeIndex] ?? THEMES[3];
    // Фон теми — на ВСІХ шарах сторінки. У style.css html і body мають height:100% та
    // власний фон: якщо задати тему лише на body, він не переходить на весь екран, а
    // малює лише перший екран заввишки (широка смуга зверху), нижче лишається темний
    // фон html, і тема видна тільки в колонці з текстом — вийшла літера "Т" (на ПК,
    // де колонка вужча за вікно). Тож фарбуємо й html, і саму колонку .app.
    document.documentElement.style.background = theme.bg;
    document.body.style.background = theme.bg;
    root.content.closest<HTMLElement>('.app')?.style.setProperty('background', theme.bg);
    root.content.style.background = theme.bg;
    root.text.style.color = theme.text;
    // Розмір тексту й відступи задаються в rem (число з налаштувань — це px при
    // базовому 16px), щоб текст розділу масштабувався разом з усім інтерфейсом
    // на моніторах (html font-size у style.css: 18/21/28/40px для 720p/1080p/
    // 1440p/4K). На телефоні html = 16px, тож 1rem = 16px і все як було.
    root.text.style.fontSize = `${settings.fontSize / 16}rem`;
    root.text.style.lineHeight = String(settings.lineHeight);
    root.text.style.textAlign = settings.align;
    // Верхній відступ рахує виріз камери (notch/punch-hole) — так само, як
    // базове значення в CSS (.reader-text у style.css), яке цей інлайновий
    // стиль тут перекриває. Саме число (settings.topOffset) тепер
    // налаштовується користувачем окремо (панель налаштувань → "Відступ
    // зверху"), бо оптимальне значення залежить від конкретного пристрою
    // (є виріз камери чи ні, є він взагалі, тощо). Лише для новел — цю
    // функцію викликає лише loadNovelContent()/renderNovelSettingsPanel(),
    // для манги/манхви є окремий renderMangaSettingsPanel() і свій
    // padding='0' у loadMangaContent().
    root.text.style.padding = `max(${settings.topOffset / 16}rem, env(safe-area-inset-top)) ${settings.padding / 16}rem 0.5rem`;
    root.text.style.fontFamily = FONT_CSS[settings.fontFamily] ?? 'inherit';
  }

  // Постійний слухач на єдиний <img> посторінкового режиму: щоразу, коли
  // showMangaPage міняє .src, знімаємо клас content-img-loaded (з'являється
  // сірий плейсхолдер), а тут повертаємо його назад, коли картинка готова.
  root.mangaImg.addEventListener('load', () => root.mangaImg.classList.add('content-img-loaded'));
  root.mangaImg.addEventListener('error', () => root.mangaImg.classList.add('content-img-loaded'));

  function showMangaPage(idx: number) {
    if (!mangaImages.length) return;
    pageIndex = Math.min(Math.max(idx, 0), mangaImages.length - 1);
    root.mangaImg.classList.remove('content-img-loaded');
    root.mangaImg.src = mangaImages[pageIndex];
    const pct = paintMangaProgress(pageIndex + 1, mangaImages.length);
    updateSliderFromValue(mangaImages.length > 1 ? pageIndex / (mangaImages.length - 1) : 1);
    saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, pct, pageIndex + 1, mangaImages.length);
    if (pct === 100) markFullyRead();
  }

  function markFullyRead() {
    saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, 100, pageIndex + 1, mangaImages.length);
  }

  const currentIndex = chapters.findIndex((c) => c.path === chapterPath);
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex >= 0 && currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;

  root.prevBtn.disabled = !prevChapter;
  root.nextBtn.disabled = !nextChapter;
  root.prevBtn.addEventListener('click', () => prevChapter && goToChapter(prevChapter));
  root.nextBtn.addEventListener('click', () => nextChapter && goToChapter(nextChapter));
  root.nextChapterFab.addEventListener('click', () => nextChapter && goToChapter(nextChapter));

  /** Поточний % прогортання сторінки (та сама формула, що й у novelScrollHandler). */
  function currentScrollPct(): number {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    return scrollable > 0 ? Math.min(100, Math.max(0, Math.round((window.scrollY / scrollable) * 100))) : 100;
  }

  // Позиція (у %), з якої стрибнули по виносці — поки що встановлена. Поки не
  // null, скрол вважається "відступом до виноски": можна вільно гортати там
  // (наприклад, прочитати кілька виносок поспіль), прогрес НЕ рахується і не
  // зберігається. Відстеження відновлюється ЛИШЕ коли позиція повертається в
  // межі ±5% від цієї точки — навіть якщо по дорозі проскролили значно далі
  // вперед (наприклад, до самого кінця розділу і трохи там погортали).
  let footnoteJumpFromPct: number | null = null;

  function footnoteReturnWatcher() {
    if (footnoteJumpFromPct === null) return;
    const pct = currentScrollPct();
    if (Math.abs(pct - footnoteJumpFromPct) <= 5) {
      footnoteJumpFromPct = null;
      suppressEndTrigger = false;
      window.removeEventListener('scroll', footnoteReturnWatcher);
    }
  }

  // Внутрішні #fragment-посилання всередині тексту розділу (типово — виноски:
  // клік на "[1]" у тексті має гортати до тексту виноски внизу сторінки, і
  // назад). Перехоплюємо клік і гортаємо вручну — інакше браузер лише міняє
  // location.hash, дописуючи фрагмент до і без того довгого URL з усіма
  // query-параметрами, що виглядає як "перехід за посиланням в нікуди", якщо
  // ціль не одразу видно на екрані.
  root.text.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a[href^="#"]');
    if (!link || !root.text.contains(link)) return;
    const fragment = decodeURIComponent(link.getAttribute('href')!.slice(1));
    if (!fragment) return;
    const dest =
      document.getElementById(fragment) || (document.getElementsByName(fragment)[0] as HTMLElement | undefined);
    if (!dest) return;
    e.preventDefault();
    if (footnoteJumpFromPct === null) {
      // Запам'ятовуємо позицію лише при ПЕРШОМУ стрибку в ланцюжку — якщо з
      // виноски перейти ще на одну, орієнтир лишається початковим місцем
      // читання, а не проміжною зупинкою.
      footnoteJumpFromPct = currentScrollPct();
      suppressEndTrigger = true;
      window.addEventListener('scroll', footnoteReturnWatcher, { passive: true });
    }
    dest.scrollIntoView({ behavior: 'smooth', block: 'center' });
    history.replaceState(null, '', `${location.pathname}${location.search}#${fragment}`);
  });

  // Кнопка підтримки в кінці розділу smakolykytl (сервер додає готовий HTML —
  // lib/support-message.ts): клік по підкресленій .support-btn показує
  // QR-коди в сусідньому .support-qr — це "хмаринка" (position:absolute у
  // style.css), що спливає НАД текстом, а не займає новий рядок. Той самий
  // делегований підхід, що й для футноток/зображень вище, — розмітка
  // приходить у innerHTML, тож власних <script>/onclick у ній нема й бути
  // не може.
  //
  // .support-btn — справжній <button> (не <span>), тож root.content
  // (нижче) вже сам ігнорує клік по ній через свою перевірку
  // .closest('a[href], button') і НЕ розгортає/згортає меню читалки. Тут
  // додатково stopPropagation() — про всяк випадок, щоб клік точно нікуди
  // не сплив, навіть якщо той виняток колись зміниться.
  root.text.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.support-btn');
    if (!btn || !root.text.contains(btn)) return;
    e.stopPropagation();
    const qr = btn.closest('.support-msg')?.querySelector('.support-qr');
    if (!qr) return;
    qr.toggleAttribute('hidden');
  });

  // ---------------- перегляд ілюстрацій новели (тап → на весь екран, збільшення пальцями) ----------------
  // Лише для novel: маска клавіатури/жестів у reader.html (viewport
  // user-scalable=no) вимикає нативний pinch-zoom сторінки, тож масштабування
  // тут реалізовано вручну через Pointer Events (працює і для тач, і для миші).
  const IV_MIN_SCALE = 1;
  const IV_MAX_SCALE = 4;
  const ivPointers = new Map<number, { x: number; y: number }>();
  let ivScale = 1;
  let ivX = 0;
  let ivY = 0;
  let ivPinchStartDistance = 0;
  let ivPinchStartScale = 1;
  let ivPinchAnchorImgX = 0;
  let ivPinchAnchorImgY = 0;
  let ivPanStart: { x: number; y: number; tx: number; ty: number } | null = null;
  let ivLastTapTime = 0;
  let ivLastTapX = 0;
  let ivLastTapY = 0;
  let ivMoved = false;

  function ivClamp() {
    // Не дає збільшеній картинці "виїхати" за межі екрана: якщо вона за
    // розміром менша за stage — центрує її й забороняє зсув; якщо більша —
    // обмежує зсув так, щоб краю не з'являлась порожнеча.
    const stageRect = root.imageViewerStage.getBoundingClientRect();
    const w = root.imageViewerImg.offsetWidth;
    const h = root.imageViewerImg.offsetHeight;
    if (!w || !h) return;
    const baseLeft = (stageRect.width - w) / 2;
    const baseTop = (stageRect.height - h) / 2;
    const scaledW = w * ivScale;
    const scaledH = h * ivScale;
    if (scaledW <= stageRect.width) {
      ivX = (stageRect.width - scaledW) / 2 - baseLeft;
    } else {
      const minX = stageRect.width - scaledW - baseLeft;
      const maxX = -baseLeft;
      ivX = Math.min(maxX, Math.max(minX, ivX));
    }
    if (scaledH <= stageRect.height) {
      ivY = (stageRect.height - scaledH) / 2 - baseTop;
    } else {
      const minY = stageRect.height - scaledH - baseTop;
      const maxY = -baseTop;
      ivY = Math.min(maxY, Math.max(minY, ivY));
    }
  }

  function ivApplyTransform() {
    ivClamp();
    root.imageViewerImg.style.transform = `translate(${ivX}px, ${ivY}px) scale(${ivScale})`;
  }

  function ivReset() {
    ivScale = 1;
    ivX = 0;
    ivY = 0;
    ivPointers.clear();
    ivPanStart = null;
    root.imageViewerImg.style.transform = '';
  }

  function openImageViewer(src: string) {
    ivReset();
    root.imageViewerImg.src = src;
    root.imageViewerOverlay.style.display = 'flex';
  }
  function closeImageViewer() {
    root.imageViewerOverlay.style.display = 'none';
    root.imageViewerImg.src = '';
    ivReset();
  }

  function ivDistance(): number {
    const pts = Array.from(ivPointers.values());
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }
  function ivMidpoint(): { x: number; y: number } {
    const pts = Array.from(ivPointers.values());
    const rect = root.imageViewerStage.getBoundingClientRect();
    return { x: (pts[0].x + pts[1].x) / 2 - rect.left, y: (pts[0].y + pts[1].y) / 2 - rect.top };
  }
  // Масштабує так, щоб точка (screenX, screenY) відносно stage лишалась
  // нерухомою під пальцем/курсором — інакше зум "тікає" убік від жесту.
  function ivZoomTo(newScaleRaw: number, screenX: number, screenY: number) {
    const newScale = Math.min(IV_MAX_SCALE, Math.max(IV_MIN_SCALE, newScaleRaw));
    const imgX = (screenX - ivX) / ivScale;
    const imgY = (screenY - ivY) / ivScale;
    ivX = screenX - imgX * newScale;
    ivY = screenY - imgY * newScale;
    ivScale = newScale;
    ivApplyTransform();
  }

  root.imageViewerImg.addEventListener('pointerdown', (e) => {
    ivMoved = false;
    ivPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    root.imageViewerImg.setPointerCapture(e.pointerId);
    if (ivPointers.size === 2) {
      ivPanStart = null;
      ivPinchStartDistance = ivDistance();
      ivPinchStartScale = ivScale;
      const mid = ivMidpoint();
      ivPinchAnchorImgX = (mid.x - ivX) / ivScale;
      ivPinchAnchorImgY = (mid.y - ivY) / ivScale;
    } else if (ivPointers.size === 1) {
      ivPanStart = { x: e.clientX, y: e.clientY, tx: ivX, ty: ivY };
    }
  });
  root.imageViewerImg.addEventListener('pointermove', (e) => {
    if (!ivPointers.has(e.pointerId)) return;
    ivPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ivPointers.size === 2) {
      ivMoved = true;
      const newDistance = ivDistance();
      if (ivPinchStartDistance <= 0) return;
      const newScale = Math.min(
        IV_MAX_SCALE,
        Math.max(IV_MIN_SCALE, ivPinchStartScale * (newDistance / ivPinchStartDistance)),
      );
      const mid = ivMidpoint();
      ivX = mid.x - ivPinchAnchorImgX * newScale;
      ivY = mid.y - ivPinchAnchorImgY * newScale;
      ivScale = newScale;
      ivApplyTransform();
    } else if (ivPointers.size === 1 && ivPanStart && ivScale > 1) {
      if (Math.hypot(e.clientX - ivPanStart.x, e.clientY - ivPanStart.y) > 4) ivMoved = true;
      ivX = ivPanStart.tx + (e.clientX - ivPanStart.x);
      ivY = ivPanStart.ty + (e.clientY - ivPanStart.y);
      ivApplyTransform();
    }
  });
  function ivPointerEnd(e: PointerEvent) {
    const wasSinglePointer = ivPointers.size === 1;
    ivPointers.delete(e.pointerId);
    if (ivPointers.size < 2) ivPinchStartDistance = 0;
    if (ivPointers.size === 0) {
      ivPanStart = null;
      // Подвійний тап (два коротких дотики близько один до одного за часом
      // і положенням) перемикає масштаб між 1× і 2.5×, без урахування
      // руху пальця під час жесту (pinch/pan) — інакше після перетягування
      // випадково спрацював би зум.
      if (wasSinglePointer && !ivMoved) {
        const now = Date.now();
        const dt = now - ivLastTapTime;
        const dist = Math.hypot(e.clientX - ivLastTapX, e.clientY - ivLastTapY);
        if (dt < 300 && dist < 30) {
          const rect = root.imageViewerStage.getBoundingClientRect();
          const sx = e.clientX - rect.left;
          const sy = e.clientY - rect.top;
          if (ivScale > 1) {
            ivX = 0;
            ivY = 0;
            ivScale = 1;
            ivApplyTransform();
          } else {
            ivZoomTo(2.5, sx, sy);
          }
          ivLastTapTime = 0;
        } else {
          ivLastTapTime = now;
          ivLastTapX = e.clientX;
          ivLastTapY = e.clientY;
        }
      }
    }
  }
  root.imageViewerImg.addEventListener('pointerup', ivPointerEnd);
  root.imageViewerImg.addEventListener('pointercancel', ivPointerEnd);
  // Колесо миші/трекпад (десктоп) — теж масштабує відносно курсора.
  root.imageViewerStage.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const rect = root.imageViewerStage.getBoundingClientRect();
      ivZoomTo(ivScale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX - rect.left, e.clientY - rect.top);
    },
    { passive: false },
  );
  root.imageViewerClose.addEventListener('click', closeImageViewer);
  // Тап по темному тлу (не по самому зображенню) закриває перегляд.
  root.imageViewerOverlay.addEventListener('click', (e) => {
    if (e.target === root.imageViewerOverlay || e.target === root.imageViewerStage) closeImageViewer();
  });

  // Тап по ілюстрації в тексті новели (не манга/манхва — там своя логіка
  // гортання сторінок) відкриває цей перегляд. stopPropagation — щоб клік не
  // долітав до root.content і не перемикав верхню/нижню панель читалки.
  root.text.addEventListener('click', (e) => {
    if (contentType !== 'novel') return;
    const img = (e.target as HTMLElement).closest('img');
    if (!img || !root.text.contains(img)) return;
    // QR підтримки з посиланням — це <a>: хай відкривається посилання, а не перегляд картинки.
    if (img.closest('.support-qr a[href]')) return;
    e.stopPropagation();
    openImageViewer((img as HTMLImageElement).currentSrc || (img as HTMLImageElement).src);
  });

  root.topBtn.addEventListener('click', () => {
    if (contentType === 'manga' && settings.mangaMode === 'paged') showMangaPage(0);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // ---------------- панель "Глави" ----------------
  // Текстовий бейдж прогресу поточного розділу — % для novel, "стор. X/Y" для manga.
  function currentProgressLabel(): string {
    if (contentType === 'manga') {
      if (settings.mangaMode === 'continuous') {
        if (!activeMangaChapterTotal) return '';
        return `стор. ${activeMangaLocalIndex + 1}/${activeMangaChapterTotal}`;
      }
      if (!mangaImages.length) return '';
      return `стор. ${pageIndex + 1}/${mangaImages.length}`;
    }
    if (lastSavedPct > 0 && lastSavedPct < 100) return `${lastSavedPct}%`;
    return '';
  }

  function renderChaptersPanel() {
    root.chaptersListPanel.innerHTML = '';
    chapters.forEach((ch) => {
      const row = document.createElement('div');
      row.className = 'chapter-item-row' + (ch.path === chapterPath ? ' current' : '');
      const progressLabel = ch.path === chapterPath ? currentProgressLabel() : '';
      const dateHtml = escapeHtml(ch.releaseTime || '') + (progressLabel ? ` <span class="ch-progress">• ${escapeHtml(progressLabel)}</span>` : '');
      row.innerHTML = `<div class="ci-name">${escapeHtml(ch.name)}</div><div class="ci-date">${dateHtml}</div>`;
      row.addEventListener('click', () => goToChapter(ch));
      root.chaptersListPanel.appendChild(row);
    });
  }
  root.chlistBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    renderChaptersPanel();
    root.chaptersOverlay.style.display = 'flex';
    const current = root.chaptersListPanel.querySelector('.current');
    current?.scrollIntoView({ block: 'center' });
  });
  root.chaptersClose.addEventListener('click', () => (root.chaptersOverlay.style.display = 'none'));
  root.chaptersOverlay.addEventListener('click', (e) => {
    if (e.target === root.chaptersOverlay) root.chaptersOverlay.style.display = 'none';
  });
  root.scrollUpBtn.addEventListener('click', () => {
    root.chaptersListPanel.scrollTo({ top: 0, behavior: 'smooth' });
  });
  root.scrollDownBtn.addEventListener('click', () => {
    root.chaptersListPanel.scrollTo({ top: root.chaptersListPanel.scrollHeight, behavior: 'smooth' });
  });

  // ---------------- панель налаштувань ----------------
  function switchSettingsTab(tab: string) {
    root.settingsTabs.forEach((t) => t.classList.toggle('active', t.dataset.tab === tab));
    (document.getElementById('settings-tab-reader') as HTMLElement).style.display = tab === 'reader' ? 'flex' : 'none';
    (document.getElementById('settings-tab-general') as HTMLElement).style.display = tab === 'general' ? 'flex' : 'none';
    (document.getElementById('settings-tab-tts') as HTMLElement).style.display = tab === 'tts' ? 'flex' : 'none';
  }
  root.settingsTabs.forEach((t) => t.addEventListener('click', () => switchSettingsTab(t.dataset.tab || 'reader')));

  // Рядок налаштування "Відступ повзунка" — спільний для новели й манги (сам
  // повзунок .v-slider показується в обох режимах), тож розмітка й прив'язка
  // подій винесені сюди, а не дублюються в обох render*SettingsPanel.
  function sliderOffsetRowHtml(): string {
    return `
      <div class="setting-row">
        <div class="setting-row-head"><span>Відступ повзунка</span><span class="val" id="so-val">${settings.sliderOffset}px</span></div>
        <input type="range" class="setting-slider" id="so-slider" min="0" max="${MAX_SLIDER_OFFSET}" step="1" value="${settings.sliderOffset}">
      </div>`;
  }
  function wireSliderOffsetRow() {
    const soSlider = document.getElementById('so-slider') as HTMLInputElement;
    soSlider.addEventListener('input', () => {
      settings.sliderOffset = Number(soSlider.value);
      (document.getElementById('so-val') as HTMLElement).textContent = `${settings.sliderOffset}px`;
      applySliderOffset();
      saveSettings(settings);
    });
  }

  function renderNovelSettingsPanel() {
    root.settingsPanelReader.innerHTML = `
      <div class="setting-row">
        <div class="setting-row-head"><span>Розмір тексту</span><span class="val" id="fs-val">${settings.fontSize}px</span></div>
        <input type="range" class="setting-slider" id="fs-slider" min="12" max="28" step="1" value="${settings.fontSize}">
      </div>
      <div class="setting-row">
        <div class="setting-row-head"><span>Колір</span></div>
        <div class="color-swatches" id="color-swatches">
          ${THEMES.map((t, i) => `<div class="color-swatch${i === settings.themeIndex ? ' active' : ''}" data-i="${i}" style="background:${t.bg};color:${t.text}">A</div>`).join('')}
        </div>
      </div>
      <div class="setting-row">
        <div class="setting-row-head"><span>Вирівнювання тексту</span></div>
        <div class="segmented" id="align-segmented">
          <button data-v="left" class="${settings.align === 'left' ? 'active' : ''}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="18" y2="18"/></svg></button>
          <button data-v="center" class="${settings.align === 'center' ? 'active' : ''}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/></svg></button>
          <button data-v="justify" class="${settings.align === 'justify' ? 'active' : ''}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg></button>
          <button data-v="right" class="${settings.align === 'right' ? 'active' : ''}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="6" y1="18" x2="20" y2="18"/></svg></button>
        </div>
      </div>
      <div class="setting-row">
        <div class="setting-row-head"><span>Міжрядковий інтервал</span><span class="val" id="lh-val">${settings.lineHeight}×</span></div>
        <input type="range" class="setting-slider" id="lh-slider" min="1" max="2.5" step="0.1" value="${settings.lineHeight}">
      </div>
      <div class="setting-row">
        <div class="setting-row-head"><span>Відступи</span><span class="val" id="pad-val">${settings.padding}px</span></div>
        <input type="range" class="setting-slider" id="pad-slider" min="0" max="48" step="2" value="${settings.padding}">
      </div>
      <div class="setting-row">
        <div class="setting-row-head"><span>Відступ зверху</span><span class="val" id="to-val">${settings.topOffset}px</span></div>
        <input type="range" class="setting-slider" id="to-slider" min="0" max="60" step="1" value="${settings.topOffset}">
        <div class="reader-setting-hint">
          Рекомендовано: ${RECOMMENDED_TOP_OFFSET}px — цього зазвичай достатньо, щоб перший рядок тексту не опинявся під вирізом камери (notch/punch-hole). Якщо на вашому екрані немає вирізу, можна зменшити.
          <span id="to-reset" style="cursor:pointer; text-decoration:underline; white-space:nowrap;">Скинути на рекомендоване</span>
        </div>
      </div>
      ${sliderOffsetRowHtml()}
      <div class="setting-row">
        <div class="setting-row-head"><span>Стиль шрифту</span></div>
        <div class="font-pills" id="font-pills">
          ${FONT_FAMILIES.map((f) => `<button class="font-pill${f === settings.fontFamily ? ' active' : ''}" data-f="${f}">${f}</button>`).join('')}
        </div>
      </div>`;

    const fsSlider = document.getElementById('fs-slider') as HTMLInputElement;
    fsSlider.addEventListener('input', () => {
      settings.fontSize = Number(fsSlider.value);
      (document.getElementById('fs-val') as HTMLElement).textContent = `${settings.fontSize}px`;
      applyTypographySettings();
      saveSettings(settings);
    });
    document.querySelectorAll('#color-swatches .color-swatch').forEach((el) => {
      el.addEventListener('click', () => {
        settings.themeIndex = Number((el as HTMLElement).dataset.i);
        themeExplicit = true;
        document.querySelectorAll('#color-swatches .color-swatch').forEach((s) => s.classList.remove('active'));
        el.classList.add('active');
        applyTypographySettings();
        saveSettings(settings);
      });
    });
    document.querySelectorAll('#align-segmented button').forEach((el) => {
      el.addEventListener('click', () => {
        settings.align = (el as HTMLElement).dataset.v as ReaderSettings['align'];
        document.querySelectorAll('#align-segmented button').forEach((b) => b.classList.remove('active'));
        el.classList.add('active');
        applyTypographySettings();
        saveSettings(settings);
      });
    });
    const lhSlider = document.getElementById('lh-slider') as HTMLInputElement;
    lhSlider.addEventListener('input', () => {
      settings.lineHeight = Number(lhSlider.value);
      (document.getElementById('lh-val') as HTMLElement).textContent = `${settings.lineHeight}×`;
      applyTypographySettings();
      saveSettings(settings);
    });
    const padSlider = document.getElementById('pad-slider') as HTMLInputElement;
    padSlider.addEventListener('input', () => {
      settings.padding = Number(padSlider.value);
      (document.getElementById('pad-val') as HTMLElement).textContent = `${settings.padding}px`;
      applyTypographySettings();
      saveSettings(settings);
    });
    const toSlider = document.getElementById('to-slider') as HTMLInputElement;
    const updateTopOffset = (v: number) => {
      settings.topOffset = v;
      toSlider.value = String(v);
      (document.getElementById('to-val') as HTMLElement).textContent = `${settings.topOffset}px`;
      applyTypographySettings();
      saveSettings(settings);
    };
    toSlider.addEventListener('input', () => updateTopOffset(Number(toSlider.value)));
    document.getElementById('to-reset')?.addEventListener('click', () => updateTopOffset(RECOMMENDED_TOP_OFFSET));
    wireSliderOffsetRow();
    document.querySelectorAll('#font-pills .font-pill').forEach((el) => {
      el.addEventListener('click', () => {
        settings.fontFamily = (el as HTMLElement).dataset.f || 'Original';
        document.querySelectorAll('#font-pills .font-pill').forEach((b) => b.classList.remove('active'));
        el.classList.add('active');
        applyTypographySettings();
        saveSettings(settings);
      });
    });
  }

  function renderMangaSettingsPanel() {
    root.settingsPanelReader.innerHTML = `
      ${sliderOffsetRowHtml()}
      <div class="reading-mode-row" id="mode-continuous" data-m="continuous">
        <div>
          <div class="rm-title">Безперервно (Webtoon)</div>
          <div class="rm-sub">Усі зображення одне під одним, гортання вниз</div>
        </div>
      </div>
      <div class="reading-mode-row" id="mode-paged" data-m="paged">
        <div>
          <div class="rm-title">Сторінками</div>
          <div class="rm-sub">Перемикання зображень свайпом/тапом, як у мангу</div>
        </div>
      </div>`;
    wireSliderOffsetRow();
    function paint() {
      (document.getElementById('mode-continuous') as HTMLElement).classList.toggle('active', settings.mangaMode === 'continuous');
      (document.getElementById('mode-paged') as HTMLElement).classList.toggle('active', settings.mangaMode === 'paged');
    }
    paint();
    document.querySelectorAll('.reading-mode-row').forEach((el) => {
      el.addEventListener('click', () => {
        const mode = (el as HTMLElement).dataset.m as ReaderSettings['mangaMode'];
        if (mode === settings.mangaMode) return;
        settings.mangaMode = mode;
        saveSettings(settings);
        paint();
        root.settingsOverlay.style.display = 'none';
        renderMangaContent(); // перерендерюємо поточний розділ у новому режимі
      });
    });
  }

  root.settingsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    switchSettingsTab('reader');
    if (contentType === 'manga') renderMangaSettingsPanel();
    else renderNovelSettingsPanel();
    root.settingsOverlay.style.display = 'flex';
  });
  root.settingsOverlay.addEventListener('click', (e) => {
    if (e.target === root.settingsOverlay) root.settingsOverlay.style.display = 'none';
  });

  // ---------------- завантаження і рендер контенту розділу ----------------
  let lastSavedPct = 0;
  // Коли true — програмний скрол (наприклад, стрибок по виносці) не повинен
  // трактуватись як "дочитав до кінця розділу": і скрол-обробник прогресу, і
  // тригер кінця сторінки це перевіряють і нічого не рахують/зберігають.
  let suppressEndTrigger = false;

  function novelScrollHandler() {
    if (suppressEndTrigger) return;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const pct = scrollable > 0 ? Math.min(100, Math.max(0, Math.round((window.scrollY / scrollable) * 100))) : 100;
    paintProgress(pct);
    updateSliderFromValue(scrollable > 0 ? window.scrollY / scrollable : 1);
    if (pct === 100 ? lastSavedPct !== 100 : Math.abs(pct - lastSavedPct) >= 5) {
      lastSavedPct = pct;
      saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, pct);
    }
  }

  /** Чи в назві розділу є "Глава N" — тоді "Розділ N" у заголовку не пишемо. */
  function titleHasGlava(title: string): boolean {
    return /глав[аи]\s*(\d+|[IVXLCDM]+)(?![\p{L}\d])/iu.test(title);
  }

  /**
   * Чи текст розділу починається з окремого рядка "Глава N.N" (без назви).
   * Тоді заголовок з номером розділу зверху зайвий — цей рядок і є заголовком.
   * Парсимо через DOMParser (а не innerHTML), щоб не завантажувати картинки зарано.
   */
  function contentStartsWithGlavaNN(html: string): boolean {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const norm = (s: string) => s.replace(/\u00a0/g, ' ').trim();
    for (const el of Array.from(doc.body.children)) {
      const text = norm(el.textContent ?? '');
      if (!text) {
        if (el.querySelector('img,video,svg')) return false; // на початку ілюстрація — це не заголовок
        continue; // порожній абзац-відступ
      }
      return /^глава\s+\d+[.,]\d+$/iu.test(text);
    }
    return false;
  }

  /** Назви-заглушки на кшталт "Глава 5.2" / "Розділ 5" — це не справжня назва розділу. */
  function isPlaceholderTitle(title: string): boolean {
    return !title || /^(глава|розділ)\s*№?\s*\d+(?:[.,]\d+)?\s*[.:)\-–—]*$/iu.test(title);
  }

  /**
   * Заголовок на початку розділу (лише smakolykytl, лише новели), усе по центру:
   *   Том N Розділ N           — більше за текст, менше за назву розділу
   *   Назва розділу            — жирним, трохи більше
   * Перший розділ тому (і том має назву):
   *   Том N: Назва тому / Розділ N / Назва розділу.
   * Якщо в назві розділу є "Глава N" — рядок "Розділ N" не виводиться.
   * Якщо текст розділу починається з рядка "Глава N.N" (без назви) — рядок "Розділ N.N" не виводиться;
   * назва розділу лишається. Якщо справжньої назви теж немає — заголовок не виводиться зовсім
   * (крім рядка "Том N: Назва тому" для першого розділу тому, якщо він є).
   * Перший розділ тому ЗАВЖДИ починається з "Том N" (навіть коли том "прихований" наскрізною нумерацією
   * чи єдиний): іменований том — "Том N: Назва тому" + "Розділ M", без назви — "Том N Розділ M".
   * Для решти розділів "Том N" пишемо лише коли нумерація розділів у томах перезапускається.
   */
  function buildChapterHeaderHtml(contentHtml: string = ''): string {
    if (pluginId !== 'smakolykytl') return '';
    const cur = chapters.find((c) => c.path === chapterPath);
    if (!cur || (cur.chapterRank === undefined && cur.chapterTitle === undefined)) return '';
    const idx = chapters.indexOf(cur);
    const prev = idx > 0 ? chapters[idx - 1] : undefined;
    const isFirstInVolume = !!cur.volumeKey && (!prev || prev.volumeKey !== cur.volumeKey);
    // Старі кеші не мають volumeShown: тоді volumeNumber був заданий лише коли "Том N" писався в кожному розділі.
    const shownEverywhere = cur.volumeShown ?? cur.volumeNumber !== undefined;
    // Перший розділ тому — номер тому є завжди; решта розділів — лише при перезапуску нумерації.
    const vol = isFirstInVolume || shownEverywhere ? cur.volumeNumber : undefined;
    const title = (cur.chapterTitle ?? '').trim();
    // Текст починається з рядка "Глава N.N" (без назви після номера) — рядок "Розділ N.N" зайвий,
    // його дублює сам текст. Назва розділу (якщо справжня) лишається в заголовку.
    const textStartsWithGlava = contentStartsWithGlavaNN(contentHtml);
    // Якщо ще й справжньої назви немає (порожня або заглушка "Глава N.N") — заголовок не виводимо взагалі.
    const skipChapterHead = textStartsWithGlava && isPlaceholderTitle(title);
    const showChapterNo = !textStartsWithGlava && !!cur.chapterRank && !titleHasGlava(title);
    const chapterNoText = showChapterNo ? `Розділ ${cur.chapterRank}` : '';

    const lines: string[] = [];
    if (isFirstInVolume && cur.volumeName) {
      lines.push(`<div class="rch-vol">${vol !== undefined ? `Том ${vol}: ` : ''}${escapeHtml(cur.volumeName)}</div>`);
      if (chapterNoText) lines.push(`<div class="rch-num">${escapeHtml(chapterNoText)}</div>`);
    } else if (!skipChapterHead || (isFirstInVolume && vol !== undefined)) {
      // Якщо "Розділ N" не виводимо (його дублює текст) — для першого розділу тому лишається самий "Том N".
      const top = [vol !== undefined ? `Том ${vol}` : '', skipChapterHead ? '' : chapterNoText].filter(Boolean).join(' ');
      if (top) lines.push(`<div class="rch-num">${escapeHtml(top)}</div>`);
    }
    if (title && !skipChapterHead) lines.push(`<div class="rch-title">${escapeHtml(title)}</div>`);
    return lines.length ? `<div class="reader-chapter-head">${lines.join('')}</div>` : '';
  }

  async function loadNovelContent() {
    sliderMode = 'scroll';
    refreshSliderLabels();
    root.text.style.display = 'block';
    root.text.classList.remove('manga-mode');
    root.text.classList.add('novel-zoomable-imgs');
    root.mangaPaged.style.display = 'none';
    applyTypographySettings();

    root.text.innerHTML = chapterLoadingHtml();
    try {
      const { content, url } = await api.chapter(pluginId!, chapterPath!, novelPath!);
      currentChapterSourceUrl = url;
      root.text.innerHTML = buildChapterHeaderHtml(content || '') + (content || '<p>Текст розділу порожній.</p>');
    } catch (err) {
      root.text.innerHTML = `<p>Не вдалося завантажити розділ: ${(err as Error).message}</p>`;
      return;
    }

    // Останній розділ новели — після всього тексту додаємо пігулку "Назад до Бібліотеки".
    if (!nextChapter) root.text.appendChild(createBackToLibraryEl('novel'));

    attachContentImageLoadHandlers(root.text);
    setupChapterEndTrigger();

    const restorePct = restoreProgressParam ? Math.min(99, Math.max(1, Number(restoreProgressParam))) : undefined;
    if (restorePct !== undefined) {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0) window.scrollTo({ top: (restorePct / 100) * scrollable });
      paintProgress(restorePct);
      lastSavedPct = restorePct;
      // Ілюстрації в тексті ще можуть довантажуватись і змінювати висоту
      // сторінки — коли вони стабілізуються, уточнюємо позицію ще раз. Але
      // тільки якщо читач сам тим часом не прогорнув сторінку.
      const scrollYAfterRestore = window.scrollY;
      waitForImages(root.text).then(() => {
        if (Math.abs(window.scrollY - scrollYAfterRestore) > 40) return;
        const sc = document.documentElement.scrollHeight - window.innerHeight;
        if (sc > 0) window.scrollTo({ top: (restorePct / 100) * sc });
      });
    } else {
      const initial = document.documentElement.scrollHeight - window.innerHeight > 0 ? 0 : 100;
      paintProgress(initial);
      lastSavedPct = initial;
      saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, initial);
    }
  }

  // ---- тригер "кінець розділу" (novel): коли невидимий маркер в самому низу
  // тексту торкається умовної лінії внизу вікна — рахуємо розділ дочитаним
  // (100%) і, якщо є наступний розділ, показуємо круглу кнопку переходу. ----
  let chapterEndObserver: IntersectionObserver | null = null;
  function setupChapterEndTrigger() {
    chapterEndObserver?.disconnect();
    const marker = document.createElement('div');
    marker.id = 'chapter-end-marker';
    marker.style.cssText = 'height:1px;';
    root.text.appendChild(marker);

    chapterEndObserver = new IntersectionObserver(
      (entries) => {
        if (suppressEndTrigger) return;
        const atEnd = !!entries[0]?.isIntersecting;
        if (atEnd) {
          paintProgress(100);
          if (lastSavedPct !== 100) {
            lastSavedPct = 100;
            saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, 100);
          }
        }
        root.nextChapterFab.style.display = atEnd && nextChapter ? 'flex' : 'none';
      },
      { root: null, rootMargin: '0px 0px -72px 0px', threshold: 0 },
    );
    chapterEndObserver.observe(marker);
  }

  let continuousLoading = false;
  let continuousChain: ChapterItem[] = [];

  // ---- прогрес/повзунок для неперервного (webtoon) режиму manga: рахуємо СТОРІНКИ, а не відсотки прогортаного ----
  function mangaContinuousScrollHandler() {
    if (allMangaImgs.length) {
      // "Активна" сторінка — та, чий верх уже перетнув умовну лінію трохи нижче верху екрана.
      const refY = Math.max(60, Math.min(window.innerHeight * 0.35, 260));
      let activeIdx = 0;
      for (let i = 0; i < allMangaImgs.length; i++) {
        if (allMangaImgs[i].el.getBoundingClientRect().top <= refY) activeIdx = i;
        else break;
      }
      // Якщо документ доскрольовано до самого низу, а верх останньої (часто короткої)
      // картинки так і не перетнув refY через брак простору для скролу — форсуємо
      // її активною, інакше остання сторінка ніколи не зарахується прочитаною.
      const atBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      if (atBottom) activeIdx = allMangaImgs.length - 1;
      const active = allMangaImgs[activeIdx];
      const pageEls = allMangaImgs.filter((e) => e.path === active.path);
      const localIdx = pageEls.findIndex((e) => e.el === active.el);
      const total = pageEls.length;

      activeMangaChapterPath = active.path;
      activeMangaChapterTotal = total;
      activeMangaLocalIndex = localIdx;
      root.vBottomLabel.textContent = String(Math.max(1, total));

      const pct = paintMangaProgress(localIdx + 1, total);
      updateSliderFromValue(total > 1 ? localIdx / (total - 1) : 1);

      if (active.path !== chapterPath) {
        const found = chapters.find((c) => c.path === active.path) || continuousChain.find((c) => c.path === active.path);
        if (found) {
          chapterPath = found.path;
          chapterName = found.name;
          chapterNumParam = found.chapterNumber !== undefined ? String(found.chapterNumber) : null;
          root.subtitle.textContent = chapterName;
          document.title = chapterName || novelName;
          history.replaceState(null, '', chapterUrl(found));
        }
      }

      if (pct === 100 ? lastSavedPct !== 100 : Math.abs(pct - lastSavedPct) >= 5) {
        lastSavedPct = pct;
        saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, pct, localIdx + 1, total);
      }
    }
    maybeLoadNextForContinuousManga();
  }

  async function maybeLoadNextForContinuousManga() {
    if (contentType !== 'manga' || settings.mangaMode !== 'continuous') return;
    if (continuousLoading) return;
    const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 600;
    if (!nearBottom) return;
    const lastLoaded = continuousChain.length ? continuousChain[continuousChain.length - 1] : chapters[currentIndex];
    const lastIdx = chapters.findIndex((c) => c.path === lastLoaded?.path);
    const next = lastIdx >= 0 && lastIdx < chapters.length - 1 ? chapters[lastIdx + 1] : null;
    if (!next) return;
    continuousLoading = true;
    try {
      const { content } = await api.chapter(pluginId!, next.path);
      const marker = document.createElement('div');
      marker.className = 'chapter-boundary-marker';
      root.text.appendChild(marker);
      const wrap = document.createElement('div');
      wrap.innerHTML = content;
      tagChapterImages(next.path, wrap);
      attachContentImageLoadHandlers(wrap);
      root.text.appendChild(wrap);
      continuousChain.push(next);
      // Щойно довантажений розділ виявився останнім у списку — під його
      // останнім зображенням (відступ 6px) додаємо пігулку "Назад до Бібліотеки".
      if (lastIdx + 1 === chapters.length - 1) {
        root.text.appendChild(createBackToLibraryEl('manga'));
      }
    } catch (err) {
      console.warn('Не вдалося довантажити наступний розділ (webtoon):', err);
    } finally {
      continuousLoading = false;
    }
  }

  function bindMangaPagedTap() {
    root.mangaPaged.onclick = (e) => {
      const rect = root.mangaPaged.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const third = rect.width / 3;
      if (x < third) {
        if (pageIndex > 0) showMangaPage(pageIndex - 1);
        else if (prevChapter) goToChapter(prevChapter);
      } else if (x > third * 2) {
        if (pageIndex < mangaImages.length - 1) showMangaPage(pageIndex + 1);
        else if (nextChapter) goToChapter(nextChapter);
        else toast('Це останній розділ');
      } else {
        toggleChrome();
      }
    };
  }

  async function renderMangaContent() {
    sliderMode = 'page'; // manga завжди рахує сторінки, а не відсотки
    window.removeEventListener('scroll', novelScrollHandler);
    window.removeEventListener('scroll', mangaContinuousScrollHandler);

    root.text.innerHTML = chapterLoadingHtml();
    // Прогрес завантаження розділу (mangarama: сервер спершу качає сторінки на диск) — у тексті під значком.
    const stopProgress = trackChapterProgress(root.text, pluginId!, chapterPath!);

    // Спроба миттєво показати список сторінок зі спільного кешу (усі
    // користувачі застосунку, прив'язано до цього конкретного розділу) —
    // той самий підхід stale-while-revalidate, що і зі списком розділів на
    // сторінці твору (novel.ts): показуємо (можливо, застарілі) сторінки
    // одразу, а живий запит унизу — незалежно від того, чи спрацював
    // кеш, — і перезаписує кеш свіжими даними, і оновлює вже показане, якщо
    // щось справді змінилось.
    let content = '';
    let url: string | undefined;
    let usedCache = false;
    try {
      const { content: cachedContent } = await api.chapterCached(pluginId!, chapterPath!);
      if (cachedContent) {
        content = cachedContent;
        usedCache = true;
      }
    } catch {
      /* немає в кеші — це нормально, чекаємо на живі дані нижче */
    }

    const livePromise = api.chapter(pluginId!, chapterPath!).catch((err) => {
      if (!usedCache) throw err;
      console.warn('Не вдалося оновити сторінки manga-розділу свіжими даними:', (err as Error).message);
      return null;
    });

    if (!usedCache) {
      try {
        const res = await livePromise;
        if (!res) throw new Error('Порожня відповідь від джерела');
        content = res.content;
        url = res.url;
        currentChapterSourceUrl = url;
      } catch (err) {
        stopProgress();
        root.text.innerHTML = `<p>Не вдалося завантажити розділ: ${(err as Error).message}</p>`;
        return;
      }
    }
    stopProgress();
    mangaImages = extractImageSrcs(content);
    refreshSliderLabels();

    // Живий запит уже в дорозі (запущений вище паралельно) — коли підійде,
    // звіряємо сторінки з тим, що вже показано з кешу, і оновлюємо лише
    // якщо вони справді відрізняються (нові/видалені сторінки на сайті).
    if (usedCache) {
      livePromise.then((res) => {
        if (!res) return; // живий запит не вдався — лишаємо показане з кешу
        currentChapterSourceUrl = res.url;
        const freshImages = extractImageSrcs(res.content);
        const changed = freshImages.length !== mangaImages.length || freshImages.some((src, i) => src !== mangaImages[i]);
        if (!changed) return; // кеш і так був актуальний
        mangaImages = freshImages;
        if (settings.mangaMode === 'continuous') {
          root.text.innerHTML = res.content || '<p>Сторінок не знайдено.</p>';
          allMangaImgs = [];
          tagChapterImages(chapterPath!, root.text);
          attachContentImageLoadHandlers(root.text);
          activeMangaChapterTotal = mangaImages.length;
          // Останній розділ манхви — під останнім зображенням (відступ 6px) додаємо пігулку "Назад до Бібліотеки".
          if (!nextChapter) root.text.appendChild(createBackToLibraryEl('manga'));
          mangaContinuousScrollHandler();
        } else {
          pageIndex = Math.min(pageIndex, Math.max(0, mangaImages.length - 1));
          showMangaPage(pageIndex);
        }
      });
    }

    if (settings.mangaMode === 'continuous') {
      root.mangaPaged.style.display = 'none';
      root.mangaPaged.onclick = null;
      root.text.style.display = 'block';
      root.text.classList.add('manga-mode');
      root.text.style.padding = '0';
      root.text.innerHTML = content || '<p>Сторінок не знайдено.</p>';
      continuousChain = [];
      allMangaImgs = [];
      const imgs = tagChapterImages(chapterPath!, root.text);
      attachContentImageLoadHandlers(root.text);
      activeMangaChapterPath = chapterPath!;
      activeMangaChapterTotal = mangaImages.length;
      // Останній розділ манхви — під останнім зображенням (відступ 6px) додаємо пігулку "Назад до Бібліотеки".
      if (!nextChapter) root.text.appendChild(createBackToLibraryEl('manga'));
      window.addEventListener('scroll', mangaContinuousScrollHandler);
      // Плейсхолдери зображень уже мають орієнтовну висоту (CSS min-height),
      // тож рахувати активну сторінку/позицію можна одразу, не чекаючи
      // фактичного довантаження картинок — саме це раніше "збивало" прогрес
      // на 100% чи скидало на початок розділу (геометрія була 0×0).
      mangaContinuousScrollHandler();
      if (restorePageParam) {
        const idx = Math.max(0, Number(restorePageParam) - 1);
        jumpToMangaPage(idx);
        // Коли саме та сторінка, на яку стрибнули, довантажиться і матиме
        // справжню висоту — уточнюємо позицію ще раз.
        const target = imgs[Math.min(idx, imgs.length - 1)];
        if (target && !(target.complete && target.naturalWidth > 0)) {
          target.addEventListener('load', () => jumpToMangaPage(idx), { once: true });
        }
      }
    } else {
      root.text.style.display = 'none';
      root.mangaPaged.style.display = 'flex';
      bindMangaPagedTap();
      showMangaPage(restorePageParam ? Number(restorePageParam) - 1 : 0);
    }
  }

  if (contentType === 'manga') {
    await renderMangaContent();
  } else {
    window.addEventListener('scroll', novelScrollHandler);
    await loadNovelContent();
  }

  window.addEventListener('beforeunload', () => {
    // ВАЖЛИВО: saveProgress повністю перезаписує запис в історії на
    // сервері (не зливає поля). Якщо тут не передати поточні page/total
    // для manga — попередньо збережений номер сторінки затирається на
    // undefined, і при наступному відкритті розділу немає що відновлювати
    // (саме це раніше й скидало читання на початок).
    if (contentType === 'manga') {
      const [page, total] =
        settings.mangaMode === 'continuous'
          ? [activeMangaLocalIndex + 1, activeMangaChapterTotal || undefined]
          : [pageIndex + 1, mangaImages.length || undefined];
      saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, lastSavedPct || 1, page, total);
    } else {
      saveProgress(pluginId!, novelPath!, chapterPath!, chapterName, chapterNumParam ? Number(chapterNumParam) : undefined, lastSavedPct || 1);
    }
  });
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
